import axios, { Canceler } from 'axios'
import { Component, Model, Prop, Ref, Vue } from 'vue-property-decorator'
import { Cropper } from 'vue-advanced-cropper'

import SiriusAPI from '@/api'
import { debounce, DebouncedFunc } from '@/lib/lodash'

import {
  CropperImage,
  CropperImageCoordinates,
  CropperImageProp,
  CropperInstance,
  CropperVisibleArea,
} from '@/types/cropper'

import {
  Image as IImage,
  ImageTransformModelId,
  SizeImage,
} from '@/types/image'
import nestedFreeze from '@/helpers/nestedFreeze'
import mergeStrings from '@/helpers/mergeStrings'
import cloneObject from '@/helpers/cloneObject'
import isEmpty from '@/helpers/isEmpty'

import ImageCropperPreview from './ImageCropperPreview/ImageCropperPreview.vue'
import CustomBackgroundWrapper from './CustomBackgroundWrapper/CustomBackgroundWrapper.vue'
import {
  calcDefaultCoordinates,
  createEmptyCopyrightImage,
  createEmptyImage,
  getCropperZoom,
  getErrorMessage,
  getZoomedSize,
  hasChangedValues,
  hasChangesInImageTransform,
  setCropCoordinates,
} from './helpers'

type ControlAction = 'rotate' | 'flip' | 'reset'
type ControlParams = Record<string, boolean | number> | null

interface ImageSourceRef {
  resetForm: () => void
  isShowConfirmForm: boolean
}

interface CropperControl {
  tooltip: string
  icon: string
  action: ControlAction
  params: ControlParams
  enabled?: boolean
}

@Component({
  components: {
    Cropper,
    CustomBackgroundWrapper,
    ImageCropperPreview,
  },
})
export default class ImageCropper extends Vue {
  @Model('change', { type: Number, default: -1 }) private value!: number
  @Prop({ type: Array, required: true })
  private readonly images!: CropperImageProp[]
  @Prop({ type: Number }) private readonly containerAspectRatio?: number
  @Prop({ type: Array }) private readonly acceptFiles?: string[]
  @Prop({ type: Boolean, default: false }) private readonly loading!: boolean
  @Prop({ type: Number })
  private readonly imageTransformModelId?: ImageTransformModelId
  @Prop({ type: Number })
  private readonly imageTransformItemId!: number
  @Prop({ type: String, default: 'blue-grey lighten-5' })
  private readonly backgroundColor!: string
  @Prop({ type: String, default: 'default' }) private readonly place!:
    | 'default'
    | 'detailImage'
  @Prop({ type: Boolean, default: false })
  private readonly hiddenControls!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly hiddenButtons!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly noGutters!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly readonlyCropper!: boolean
  @Prop({ type: String, default: '' })
  private readonly titleOneAllImages!: string
  @Prop({ type: String, default: '' })
  private readonly title!: string
  @Prop({ type: String, default: '' })
  private readonly infoText!: string
  @Prop({ type: Number }) private readonly maxSize?: number
  @Prop({ type: Boolean, default: true })
  private readonly switchEnabled!: boolean
  @Prop({ type: Boolean, default: true })
  private readonly buttonsEnabled!: boolean
  @Prop({ type: Boolean, default: true })
  private readonly isActiveCropper!: boolean
  @Prop({ type: Boolean, default: undefined })
  private readonly oneImageForAllFormats?: boolean
  @Prop({ type: Boolean, default: false })
  private readonly hidePreviews!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly isShowCloseButton!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly enabledSource!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly sourceNonRequired!: boolean
  @Prop({ type: String, default: '' })
  private readonly defaultSource!: string

  @Ref('cropper') private readonly cropperRef?: CropperInstance
  @Ref('imageSource') private readonly imageSourceRef?: ImageSourceRef

  private readonly zoomStep = 0.01

  private cropperKey = 1
  private isLoading = false
  private isReadyCropper = false
  private stencilAspectRatio = 0
  private currentOriginalImageUrl = ''
  private objectURLImages: string[] = []
  private currentImageIndex = -1
  private localOneImageForAllFormats = false
  private currentZoom = 0
  private initialImages: CropperImageProp[] = []
  private localImages: CropperImageProp[] = []
  private queueSaveRequestKeys: Record<string, Canceler | boolean> = {}
  private errorSaveRequestKeys: string[] = []
  private successSaveRequestKeys: string[] = []
  private forceImageSourceKey = Date.now()
  private forceRecreateCropperKey = 1
  private uploadedImageUrls = new Map<number, string>()
  private onReady!: DebouncedFunc<() => void>

  private imageSize = {
    width: 666,
    height: 444,
  }

  private visibleArea = {
    width: 666,
    height: 444,
  }

  private get localIsLoading() {
    return this.loading || this.isLoading
  }

  private get imageContainerAspectRatio() {
    if (this.place === 'detailImage') {
      return 1.5
    }

    if (this.containerAspectRatio) {
      return this.containerAspectRatio
    }

    return this.visibleArea.width / this.visibleArea.height
  }

  private get changedSourceImageTransforms() {
    if (!this.enabledSource) {
      return
    }

    const map = new Map<string, CropperImageProp[]>()

    this.localImages.forEach((newImage, index) => {
      const imageUrl = newImage.transform.image?.url

      if (!imageUrl) {
        return
      }

      const oldImage = this.initialImages[index]
      const oldCopyright = oldImage?.transform.image?.copyrightImage
      const newCopyright = newImage.transform.image?.copyrightImage

      const hasChanges = hasChangedValues([
        [oldCopyright?.source || null, newCopyright?.source || null],
        [oldCopyright?.source_url || null, newCopyright?.source_url || null],
        [oldCopyright?.url || null, newCopyright?.url || null],
        [
          oldCopyright?.copyright_file || null,
          newCopyright?.copyright_file || null,
        ],
        [
          oldImage?.transform.image?.id || null,
          newImage.transform.image?.id || null,
        ],
      ])

      if (!hasChanges && !this.sourceNonRequired) {
        return
      }

      const group = map.get(imageUrl)

      if (group) {
        group.push(newImage)
      } else {
        map.set(imageUrl, [newImage])
      }
    })

    return map
  }

  private get hasChanges() {
    if (this.changedSourceImageTransforms?.size) {
      return true
    }

    if (!this.localImages.length || !this.initialImages.length) {
      return false
    }

    return this.localImages.some((newImage, index) =>
      this.shouldSaveImageTransform(newImage, this.initialImages[index])
    )
  }

  private get currentImage() {
    return (
      this.localImages[this.currentImageIndex] ||
      this.localImages.find(({ transform: { image_id } }) => !!image_id) ||
      this.localImages[0] ||
      null
    )
  }

  private get isSaving() {
    return !isEmpty(this.queueSaveRequestKeys)
  }

  private get isDynamicRatio() {
    return !this.currentImage?.aspectRatio
  }

  private get minImageSize() {
    if (!this.localOneImageForAllFormats) {
      return {
        width: this.currentImage?.width,
        height: this.currentImage?.height,
      }
    }

    let width = 0
    let height = 0

    this.localImages.forEach((localImage) => {
      width = Math.max(width, localImage.width)
      height = Math.max(height, localImage.height)
    })

    return {
      width,
      height,
    }
  }

  private get currentAspectRatio() {
    if (!this.currentImage) {
      return
    }

    if (this.place === 'detailImage' && this.currentImageIndex === -1) {
      return this.imageContainerAspectRatio
    }

    return (
      this.currentImage.aspectRatio ??
      this.imageSize.width / this.imageSize.height
    )
  }

  private get canSaveImage() {
    return !!this.currentImage?.transform.image?.url
  }

  private get imageSourceKey() {
    return `${this.forceImageSourceKey}_${this.currentImageIndex}`
  }

  private get controlsConfig() {
    const controls: CropperControl[] = [
      {
        tooltip: 'Поворот против часовой',
        icon: 'mdi-rotate-left',
        action: 'rotate',
        params: { angle: -90 },
        enabled: false,
      },
      {
        tooltip: 'Поворот по часовой',
        icon: 'mdi-rotate-right',
        action: 'rotate',
        params: { angle: 90 },
        enabled: false,
      },
      {
        tooltip: 'Отразить по горизонтали',
        icon: 'mdi-swap-horizontal',
        action: 'flip',
        params: { vertical: false, horizontal: true },
        enabled: false,
      },
      {
        tooltip: 'Отразить по вертикали',
        icon: 'mdi-swap-vertical',
        action: 'flip',
        params: { vertical: true, horizontal: false },
        enabled: false,
      },
      {
        tooltip:
          this.localOneImageForAllFormats && this.currentImageIndex === -1
            ? 'Удалить все картинки'
            : 'Удалить',
        icon: 'mdi-delete-outline',
        action: 'reset',
        params: null,
      },
    ]

    return controls.filter(({ enabled = true }) => enabled)
  }

  private created() {
    this.onReady = debounce(() => {
      if (!this.cropperRef) {
        return
      }

      this.updateCurrentZoom()

      this.isReadyCropper = true

      this.onChange(this.cropperRef.getResult())
    }, 100)

    this.$watch(
      () => this.isActiveCropper,
      (isActiveCropper) => {
        this.onReady.cancel()

        if (!isActiveCropper) {
          this.isReadyCropper = false
          return
        }

        this.initImages()

        this.currentImageIndex =
          this.value > -1 ? this.value : this.getDefaultImageIndex()
      },
      {
        immediate: true,
      }
    )

    this.$watch(
      () =>
        [
          this.currentAspectRatio,
          this.currentImageIndex,
          this.localOneImageForAllFormats,
          this.currentImage?.transform.image?.url,
          this.forceRecreateCropperKey,
        ].join('-'),
      () => {
        this.onReady.cancel()

        this.isReadyCropper = false
        this.currentOriginalImageUrl =
          this.currentImage?.transform.image?.url || ''
        this.stencilAspectRatio = this.currentAspectRatio || 0
        this.cropperKey++
      },
      { immediate: true }
    )
  }

  private mounted() {
    this.$watch(
      () => this.isSaving,
      (isSaving) => {
        if (!isSaving) {
          this.emitSavedImages()
        }

        this.$emit('update:loading', isSaving)
        this.$emit('update:saving', isSaving)
      }
    )

    this.$watch(
      () => {
        return this.currentImageIndex
      },
      (newIndex) => {
        this.$emit('change', newIndex)
      }
    )

    this.$emit('ready')
  }

  private beforeDestroy() {
    this.onReady.cancel()
    this.$emit('update:loading', false)
    this.$emit('update:saving', false)

    this.objectURLImages.forEach((objectURLImage) => {
      URL.revokeObjectURL(objectURLImage)
    })
  }

  private initImages() {
    if (this.isSaving) {
      return
    }

    this.initialImages = cloneObject(this.images)

    this.toggleOneImageForAllFormats(
      this.initialImages.every(
        (image) =>
          image.transform.image?.id ===
          this.initialImages[0].transform.image?.id
      )
    )

    this.localImages = this.initialImages.map((image) => {
      const transform = cloneObject(image.transform)

      if (transform.image?.url) {
        transform.image.copyrightImage =
          transform.image.copyrightImage || createEmptyCopyrightImage()

        if (
          isEmpty(transform.image.copyrightImage.source) &&
          !isEmpty(this.defaultSource)
        ) {
          transform.image.copyrightImage.source = this.defaultSource
        }
      }

      return {
        ...image,
        transform,
      }
    })
  }

  private resetCopyrightForm() {
    this.imageSourceRef?.resetForm()
  }

  private getDefaultImageIndex() {
    if (this.localImages.length === 1) {
      return 0
    }

    const isEmptyEveryImage = this.localImages.every(
      (localImage) => !localImage.transform.image?.url
    )

    if (isEmptyEveryImage) {
      return -1
    }

    const initialAspectRatio =
      this.containerAspectRatio ?? this.imageContainerAspectRatio

    const defaultImageIndex = this.localImages.findIndex(
      (image) => image.aspectRatio === initialAspectRatio
    )

    return Math.max(defaultImageIndex, 0)
  }

  private shouldSaveImageTransform(
    localImage: CropperImageProp,
    initialImage?: CropperImageProp
  ) {
    return (
      !initialImage ||
      this.errorSaveRequestKeys.includes(initialImage.key) ||
      hasChangesInImageTransform(initialImage, localImage)
    )
  }

  private toggleOneImageForAllFormats(val = !this.localOneImageForAllFormats) {
    this.localOneImageForAllFormats = this.oneImageForAllFormats ?? val
  }

  private stencilSize({
    boundaries,
  }: {
    boundaries: { width: number; height: number }
  }) {
    if (!this.isDynamicRatio || !this.cropperRef) {
      return {
        width: boundaries.width,
        height: boundaries.height,
      }
    }

    const aspectRatio =
      this.cropperRef.imageSize.width / this.cropperRef.imageSize.height

    return {
      width: Math.floor(boundaries.height * aspectRatio),
      height: boundaries.height,
    }
  }

  private defaultBoundaries({ cropper }: { cropper: HTMLDivElement }) {
    return {
      width: cropper.clientWidth,
      height: cropper.clientHeight,
    }
  }

  private defaultSize() {
    if (!this.currentImage || !this.cropperRef) {
      return
    }

    const { imageSize } = this.cropperRef
    const { transform } = this.currentImage

    if (this.isDynamicRatio) {
      const cropW = transform.crop_w || 0
      const cropH = transform.crop_h || 0
      const isLandscape = imageSize.width > imageSize.height

      const width = isLandscape
        ? cropW
        : cropH * (imageSize.height / imageSize.width)

      const height = isLandscape
        ? cropW * (imageSize.width / imageSize.height)
        : cropH

      return {
        width: Math.round(Math.min(width, imageSize.width)),
        height: Math.round(Math.min(height, imageSize.height)),
      }
    }

    if (
      this.currentAspectRatio === this.currentImage.aspectRatio ||
      this.currentImageIndex !== -1
    ) {
      if (transform.crop_w && transform.crop_h) {
        return {
          width: transform.crop_w,
          height: transform.crop_h,
        }
      }
    }

    const { crop_w, crop_h } = calcDefaultCoordinates({
      aspectRatio: this.currentAspectRatio,
      imgWidth: imageSize.width,
      imgHeight: imageSize.height,
    })

    return {
      width: crop_w,
      height: crop_h,
    }
  }

  private defaultPosition() {
    if (!this.currentImage || !this.cropperRef) {
      return
    }

    const { boundaries, imageSize } = this.cropperRef
    const { transform } = this.currentImage

    if (this.isDynamicRatio) {
      const cropX = transform.crop_x || 0
      const cropY = transform.crop_y || 0
      const cropW = transform.crop_w || 0
      const cropH = transform.crop_h || 0

      let left = cropX
      let top = cropY

      if (imageSize.width > imageSize.height) {
        const cropHeight = cropW * (imageSize.height / imageSize.width)
        const visibleHeight = cropW * (boundaries.height / boundaries.width)

        top = Math.round(
          cropY > 0
            ? (visibleHeight - cropHeight) / 2 + cropY
            : (imageSize.height - cropHeight) / 2
        )
      } else {
        const cropWidth = cropH * (imageSize.width / imageSize.height)
        const visibleWidth = cropH * (boundaries.width / boundaries.height)

        left = Math.round(
          cropX > 0
            ? (visibleWidth - cropWidth) / 2 + cropX
            : (imageSize.width - cropWidth) / 2
        )
      }

      return {
        left: Math.max(left, 0),
        top: Math.max(top, 0),
      }
    }

    const { crop_x: left, crop_y: top } = calcDefaultCoordinates({
      aspectRatio: this.currentAspectRatio,
      imgWidth: imageSize.width,
      imgHeight: imageSize.height,
    })

    if (
      this.currentImageIndex === -1 &&
      this.currentAspectRatio !== this.currentImage.aspectRatio
    ) {
      return {
        top,
        left,
      }
    }

    return {
      top: transform.crop_y ?? top,
      left: transform.crop_x ?? left,
    }
  }

  private onChange({
    coordinates,
    image,
    visibleArea,
  }: {
    coordinates: CropperImageCoordinates
    image: CropperImage
    visibleArea?: CropperVisibleArea
  }) {
    if (
      !this.isReadyCropper ||
      !this.currentImage ||
      !visibleArea ||
      !this.cropperRef
    ) {
      return
    }

    this.updateCurrentZoom()

    if (this.isDynamicRatio && this.currentImageIndex !== -1) {
      const { imageSize } = this.cropperRef

      setCropCoordinates(this.currentImage.transform, {
        crop_h: Math.round(Math.min(visibleArea.height, imageSize.height)),
        crop_w: Math.round(Math.min(visibleArea.width, imageSize.width)),
        crop_x: Math.round(Math.max(visibleArea.left, 0)),
        crop_y: Math.round(Math.max(visibleArea.top, 0)),
      })

      return
    }

    const imageForAllFormats = this.localOneImageForAllFormats
      ? this.currentImage.transform.image ||
        this.localImages.find(({ transform: { image } }) => image?.url)
          ?.transform.image ||
        null
      : null

    const left = Math.round(Math.max(coordinates.left, 0))
    const top = Math.round(Math.max(coordinates.top, 0))

    this.localImages.forEach((localImage, imageIndex) => {
      const transformImage = imageForAllFormats || localImage.transform.image

      if (this.currentImageIndex === imageIndex) {
        setCropCoordinates(localImage.transform, {
          crop_x: left,
          crop_y: top,
          crop_w: coordinates.width,
          crop_h: coordinates.height,
        })
      } else if (
        this.currentImageIndex === -1 ||
        (this.localOneImageForAllFormats &&
          localImage.transform.image?.url !== transformImage?.url)
      ) {
        setCropCoordinates(
          localImage.transform,
          this.calcLinkedCoordinates({
            zoom: this.currentZoom,
            aspectRatio: localImage.aspectRatio,
            image,
            coordinates: {
              left,
              top,
              width: coordinates.width,
              height: coordinates.height,
            },
          })
        )
      }

      localImage.transform.image = transformImage || null
      localImage.transform.image_id = transformImage?.id || null
    })

    this.imageSize.width = image.width
    this.imageSize.height = image.height

    this.visibleArea.width = Math.floor(visibleArea.width)
    this.visibleArea.height = Math.floor(visibleArea.height)
  }

  private calcLinkedCoordinates(params: {
    zoom: number
    aspectRatio: number
    image: CropperImage
    coordinates: CropperImageCoordinates
  }) {
    const isRotated = Math.abs(params.image.transforms.rotate) % 180 > 0
    const imgWidth = isRotated ? params.image.height : params.image.width
    const imgHeight = isRotated ? params.image.width : params.image.height

    const offsetLeft =
      params.coordinates.left / (imgWidth - params.coordinates.width)
    const offsetTop =
      params.coordinates.top / (imgHeight - params.coordinates.height)

    return calcDefaultCoordinates({
      imgWidth,
      imgHeight,
      zoom: params.zoom,
      aspectRatio: params.aspectRatio,
      offsetLeft: Number.isNaN(offsetLeft) ? 0.5 : offsetLeft,
      offsetTop: Number.isNaN(offsetTop) ? 0.5 : offsetTop,
    })
  }

  private updateCurrentZoom() {
    const cropper = this.cropperRef

    if (!cropper) {
      return
    }

    const { coordinates, imageSize } = cropper
    const isImageWider =
      imageSize.width / imageSize.height >
      coordinates.width / coordinates.height

    this.currentZoom = getCropperZoom({
      imageSize: cropper.imageSize,
      sizeRestrictions: cropper.sizeRestrictions,
      coordinates: cropper.coordinates,
      side: isImageWider ? 'height' : 'width',
    })
  }

  private onZoom(value: number) {
    const currentValue =
      Math.round((this.currentZoom * 100) / this.zoomStep) * this.zoomStep

    if (Math.abs(value - currentValue) < this.zoomStep / 2) {
      return
    }

    const oldZoom = this.currentZoom
    const newZoom = value / 100

    this.currentZoom = newZoom

    const cropper = this.cropperRef

    if (!cropper) {
      return
    }

    const side =
      cropper.imageSize.height < cropper.imageSize.width ? 'height' : 'width'

    const zoomFactor =
      getZoomedSize({
        side,
        imageSize: cropper.imageSize,
        sizeRestrictions: cropper.sizeRestrictions,
        zoom: oldZoom,
      }) /
      getZoomedSize({
        side,
        imageSize: cropper.imageSize,
        sizeRestrictions: cropper.sizeRestrictions,
        zoom: newZoom,
      })

    cropper.zoom(zoomFactor)
  }

  private controlAction(action: ControlAction, params: ControlParams = null) {
    switch (action) {
      case 'reset':
        this.onReplacePreview(this.currentImageIndex)
        break
      case 'flip':
        this.cropperRef?.flip(!!params?.horizontal, !!params?.vertical)
        break
      case 'rotate':
        this.cropperRef?.rotate(Number(params?.angle))
        break
    }
  }

  private onClickPreview(e: Event, index: number) {
    const target = e.target as HTMLElement | null

    if (target?.closest('.v-btn')) {
      return
    }

    const firstImageUrl = this.localImages[0].transform.image?.url
    const isImageForAllFormats = this.localImages.every(
      ({ transform: { image } }) => image?.url === firstImageUrl
    )

    const nextImageIndex =
      isImageForAllFormats && this.currentImageIndex === index ? -1 : index

    if (nextImageIndex !== this.currentImageIndex) {
      this.currentZoom = 0
      this.currentImageIndex = nextImageIndex
    }
  }

  private onCropPreview(index: number) {
    this.currentImageIndex = index
  }

  private onReplacePreview(index: number) {
    this.currentImageIndex = index

    if (index === -1) {
      this.localImages.forEach((localImage) => {
        this.resetImageTransform(localImage)
      })
    } else {
      this.resetImageTransform(this.localImages[index])
      this.toggleOneImageForAllFormats(false)
    }

    this.resetCopyrightForm()
    this.currentZoom = 0
  }

  private resetImageTransform({ transform }: CropperImageProp) {
    setCropCoordinates(transform, {
      crop_y: null,
      crop_x: null,
      crop_w: null,
      crop_h: null,
    })
    transform.image_id = null
    transform.settings = null
    transform.image = createEmptyImage()
  }

  private onLoadStart() {
    this.$emit('load:start')
  }

  private onLoadFile(params: { file?: File; image?: SizeImage }) {
    if (params.file) {
      this.loadImageFile(params.file)
    }

    if (params.image) {
      this.onLoadImage({
        from: params.image.from,
        width: params.image.width,
        height: params.image.height,
        id: params.image.id,
        url: params.image.url,
        copyrightImage:
          params.image.copyrightImage || createEmptyCopyrightImage(),
      })

      this.forceImageSourceKey++
    }
  }

  private loadImageFile(file: File) {
    const imageUrl = URL.createObjectURL(file)
    const image = new Image()

    this.objectURLImages.push(imageUrl)

    image.onload = () => {
      this.onLoadImage({
        width: image.width,
        height: image.height,
        id: 0,
        url: imageUrl,
        file,
        copyrightImage: createEmptyCopyrightImage(),
      })
    }

    image.onerror = () => {
      this.$notify({
        type: 'error',
        title: 'Не удалось загрузить изображение',
      })
    }

    image.src = imageUrl
  }

  private onLoadImage({
    url,
    id,
    width,
    height,
    file,
    copyrightImage,
    from,
    ...payload
  }: SizeImage) {
    if (!this.currentImage || this.currentOriginalImageUrl === url) {
      return
    }

    this.imageSize.width = width
    this.imageSize.height = height
    this.currentZoom = 0

    const applyImage = (localImage: CropperImageProp, aspectRatio?: number) => {
      localImage.transform.image = { url, id, file, copyrightImage, from }
      localImage.transform.image_id = id

      setCropCoordinates(
        localImage.transform,
        calcDefaultCoordinates({
          aspectRatio,
          imgWidth: width,
          imgHeight: height,
        })
      )
    }

    if (this.currentImageIndex > -1 && !this.oneImageForAllFormats) {
      applyImage(
        this.localImages[this.currentImageIndex],
        this.currentAspectRatio
      )
    } else {
      this.localImages.forEach((localImage) => {
        applyImage(localImage, localImage.aspectRatio)
      })
    }

    this.forceRecreateCropperKey++

    if (this.enabledSource) {
      return
    }

    this.$emit('load:image', {
      ...payload,
      url,
      id,
      width,
      height,
    })
  }

  private onUpdateImageSource(params: {
    imageUrl: string
    imageId?: number
    source?: string
    sourceUrl: string
    file?: File
    copyrightImageUrl?: string
  }) {
    this.localImages.forEach(({ transform: { image } }) => {
      if (
        !image ||
        (image.id !== params.imageId && image.url !== params.imageUrl)
      ) {
        return
      }

      image.copyrightImage = image.copyrightImage || createEmptyCopyrightImage()

      const { copyrightImage } = image

      if (params.file !== undefined) {
        URL.revokeObjectURL(copyrightImage.url)

        const copyrightFileUrl = params.file
          ? URL.createObjectURL(params.file)
          : null

        if (copyrightFileUrl) {
          this.objectURLImages.push(copyrightFileUrl)
        }

        this.$set(copyrightImage, 'copyright_file', params.file)
        this.$set(copyrightImage, 'url', copyrightFileUrl)
      }

      if (params.source !== undefined) {
        this.$set(copyrightImage, 'source', params.source)
      }

      if (params.sourceUrl !== undefined) {
        this.$set(copyrightImage, 'source_url', params.sourceUrl)
      }

      if (params.copyrightImageUrl !== undefined) {
        URL.revokeObjectURL(copyrightImage.url)

        this.$set(copyrightImage, 'url', params.copyrightImageUrl)
      }
    })
  }

  private validateImages() {
    if (!this.enabledSource) {
      return ''
    }

    const currentImageUrl = this.currentImage?.transform.image?.url
    const isConfirmFormShown = !!this.imageSourceRef?.isShowConfirmForm

    const invalidImage = this.localImages.find(({ transform }) => {
      if (!transform.image?.url) {
        return false
      }

      if (
        this.sourceNonRequired &&
        currentImageUrl === transform.image.url &&
        !isConfirmFormShown
      ) {
        return false
      }

      const copyrightImage = transform.image.copyrightImage

      if (!copyrightImage?.source) {
        return !this.sourceNonRequired
      }

      if (copyrightImage.source_url) {
        return !(copyrightImage.copyright_file || copyrightImage.url)
      }

      return false
    })

    if (!invalidImage) {
      return ''
    }

    return `Необходимо заполнить поля, относящиеся к источнику фото для кропа «${
      invalidImage.description || invalidImage.title
    }»`
  }

  private async uploadImageSource(image: IImage): Promise<IImage | null> {
    const { copyrightImage } = image

    if (image.id) {
      if (!copyrightImage?.source) {
        return null
      }

      const { item } = await SiriusAPI.image.updateImageSource({
        id: image.id,
        source: copyrightImage.source,
        source_url: copyrightImage.source_url || undefined,
        copyright: copyrightImage.copyright_file || undefined,
      })

      item.from = image.from

      return item
    }

    if (!image.file || !(copyrightImage?.source || this.sourceNonRequired)) {
      return null
    }

    const formData = new FormData()

    formData.append('file', image.file)

    if (copyrightImage?.source) {
      formData.append('source', copyrightImage.source)
    }

    if (copyrightImage?.copyright_file) {
      formData.append('copyright', copyrightImage.copyright_file)
      formData.append('source_url', copyrightImage.source_url)
    }

    const { item } = await SiriusAPI.image.addImage({ file: formData })

    this.$emit('load:image', item)

    return item
  }

  private async saveChangedImageSources() {
    const changedSourceImageTransforms = this.changedSourceImageTransforms

    if (!changedSourceImageTransforms?.size) {
      return true
    }

    this.isLoading = true

    try {
      for (const imageTransforms of changedSourceImageTransforms.values()) {
        const image = imageTransforms.find(({ transform }) =>
          this.sourceNonRequired
            ? transform.image
            : transform.image?.copyrightImage?.source
        )?.transform.image

        if (!image) {
          continue
        }

        let newImage: IImage | null = null

        try {
          newImage = await this.uploadImageSource(image)
        } catch (error) {
          this.notifyImageSourceError(error, imageTransforms)

          return false
        }

        if (!newImage?.id) {
          continue
        }

        if (!newImage.copyrightImage) {
          newImage.copyrightImage = createEmptyCopyrightImage()
        }

        this.uploadedImageUrls.set(newImage.id, newImage.url)

        for (const { transform } of imageTransforms) {
          transform.image = {
            ...newImage,
            url: transform.image?.url || newImage.url,
          }

          transform.image_id = newImage.id
        }
      }

      return true
    } finally {
      this.isLoading = false
    }
  }

  private notifyImageSourceError(
    error: unknown,
    imageTransforms: CropperImageProp[]
  ) {
    const cropDescriptions = this.formatCropDescriptions(
      new Set(imageTransforms.map(({ key }) => key))
    )

    this.$notify({
      title: `Ошибка в ${cropDescriptions}:<br /><br />${getErrorMessage(
        error
      )}`,
      type: 'error',
      autoCloseTime: 5e3,
    })
  }

  private async onSave() {
    const errorMessage = this.validateImages()

    if (errorMessage) {
      this.$notify({
        type: 'error',
        title: errorMessage,
      })

      return
    }

    const isSourcesSaved = await this.saveChangedImageSources()

    if (!isSourcesSaved) {
      return
    }

    this.localImages
      .filter((localImage) =>
        this.shouldSaveImageTransform(
          localImage,
          this.initialImages.find(({ key }) => key === localImage.key)
        )
      )
      .forEach((localImage) => {
        this.saveImage(localImage)
      })

    const savedImages = this.localImages.map((localImage) =>
      this.toSavedImage(localImage)
    )

    this.initialImages = nestedFreeze(cloneObject(savedImages))

    this.$emit('save:local', savedImages)
  }

  private onCancel() {
    this.isReadyCropper = false
    this.onReady.cancel()

    this.$emit('cancel')
    this.initImages()

    this.currentImageIndex =
      this.value > -1 ? this.value : this.getDefaultImageIndex()

    this.forceRecreateCropperKey++
  }

  private saveImage(localImage: CropperImageProp) {
    const {
      key: cropImageKey,
      transform: { image_id: imageId, id },
    } = localImage

    const canceler = this.queueSaveRequestKeys[cropImageKey]

    if (typeof canceler === 'boolean') {
      return
    }

    if (typeof canceler === 'function') {
      canceler()
    }

    this.errorSaveRequestKeys = this.errorSaveRequestKeys.filter(
      (key) => key !== cropImageKey
    )

    this.$set(this.queueSaveRequestKeys, cropImageKey, true)

    if (!imageId) {
      this.$nextTick(() => {
        this.$delete(this.queueSaveRequestKeys, cropImageKey)
      })

      return
    }

    const payload = {
      crop_h: localImage.transform.crop_h,
      crop_w: localImage.transform.crop_w,
      crop_x: localImage.transform.crop_x,
      crop_y: localImage.transform.crop_y,
      image_id: imageId,
      model_id: this.imageTransformModelId,
      item_id: this.imageTransformItemId,
      isShowNotify: false,
    }

    let requestPromise: Promise<unknown>

    if (id) {
      const cancelTokenSource = axios.CancelToken.source()

      this.$set(
        this.queueSaveRequestKeys,
        cropImageKey,
        cancelTokenSource.cancel
      )

      requestPromise = SiriusAPI.image.changeImageTransform({
        ...payload,
        id,
        responseStatusOnly: 1,
        cancelToken: cancelTokenSource.token,
      })
    } else {
      requestPromise = SiriusAPI.image
        .addImageTransform(payload)
        .then(({ item }) => {
          localImage.transform.id = item.id
        })
    }

    requestPromise
      .then(() => {
        this.$delete(this.queueSaveRequestKeys, cropImageKey)

        const hasChanges = hasChangedValues([
          [payload.crop_w, localImage.transform.crop_w],
          [payload.crop_h, localImage.transform.crop_h],
          [payload.crop_x, localImage.transform.crop_x],
          [payload.crop_y, localImage.transform.crop_y],
          [imageId, localImage.transform.image_id],
        ])

        if (hasChanges) {
          this.saveImage(localImage)
        } else {
          this.successSaveRequestKeys.push(cropImageKey)
          this.notifySaveResult()
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          return
        }

        this.$delete(this.queueSaveRequestKeys, cropImageKey)
        this.errorSaveRequestKeys.push(cropImageKey)

        this.notifySaveResult()
      })
      .finally(() => {
        if (this.isSaving) {
          return
        }

        this.successSaveRequestKeys = []

        if (this.errorSaveRequestKeys.length) {
          this.$emit('save:error')
        }
      })
  }

  private toSavedImage(localImage: CropperImageProp) {
    const savedImage = cloneObject(localImage)
    const { image } = savedImage.transform
    const uploadedImageUrl = image && this.uploadedImageUrls.get(image.id)

    if (image && uploadedImageUrl) {
      image.url = uploadedImageUrl
    }

    return savedImage
  }

  private emitSavedImages() {
    const savedImages = this.localImages.map((localImage) =>
      this.toSavedImage(localImage)
    )

    savedImages.forEach((savedImage) => {
      if (!savedImage.transform.image_id) {
        savedImage.transform.id = null
      }

      this.$emit('save', savedImage)
    })

    this.$emit('save:success', savedImages)
  }

  private formatCropDescriptions(keys: Set<string>) {
    const descriptionParts: string[] = []

    keys.forEach((key) => {
      const description = this.localImages.find(
        (image) => image.key === key
      )?.description

      if (description) {
        descriptionParts.push(`«${description}»`)
      }
    })

    return descriptionParts.join(', ')
  }

  private notifySaveResult() {
    if (this.isSaving) {
      return
    }

    const successKeys = new Set(this.successSaveRequestKeys)
    const errorKeys = new Set(this.errorSaveRequestKeys)

    if (successKeys.size) {
      const isPlural = successKeys.size > 1

      this.$notify({
        title: mergeStrings({
          values: [
            isPlural ? 'Кропы' : 'Кроп',
            this.formatCropDescriptions(successKeys),
            isPlural ? 'сохранены' : 'сохранен',
          ],
        }),
        type: 'success',
        autoCloseTime: 7e3,
      })
    }

    if (errorKeys.size) {
      this.$notify({
        type: 'error',
        title: mergeStrings({
          values: [
            errorKeys.size > 1
              ? 'Ошибка сохранения кропов'
              : 'Ошибка сохранения кропа',
            this.formatCropDescriptions(errorKeys),
          ],
        }),
        autoCloseTime: 0,
      })
    }
  }

  private closeCropper() {
    this.$emit('close')
  }
}
